import { computed, Injectable, Signal, signal } from '@angular/core'
import {
	BankOfferExtraCurrencyTypes,
	BankOfferTypes,
	isEventType,
	isFreebie,
	ShopBankOfferDto,
	ShopConfigDto,
} from '@mos/api/bank/dto-client'

export interface ShopBankEventOffers {
	name: string
	offers: ShopBankOfferDto[]
}

@Injectable({ providedIn: 'root' })
export class ShopRepository {
	private readonly store = signal<ShopConfigDto>({ rewardedVideos: 0, offers: [] })

	get state() {
		return this.store()
	}

	setConfig(config: ShopConfigDto) {
		this.store.set(config)
	}

	removeOffer(offerId: number) {
		this.store.update((state) => ({
			...state,
			offers: state.offers.filter((offer) => offer.id !== offerId),
		}))
	}

	setRewardedVideos(rewardedVideos: number) {
		this.store.update((state) => ({ ...state, rewardedVideos }))
	}

	rewardedVideos = computed(() => this.store().rewardedVideos)

	shopOffers: Signal<ShopBankOfferDto[]> = computed(() => this.store().offers)

	keyOffers: Signal<ShopBankOfferDto[]> = computed(() =>
		this.shopOffers()
			.filter((offer) => offer.type === BankOfferTypes.KEYS && offer.keys !== null && !isFreebie(offer.currency))
			.sort((a, b) => (a.keys ?? 0) - (b.keys ?? 0)),
	)

	keyFreebies: Signal<ShopBankOfferDto[]> = computed(() =>
		this.shopOffers()
			.filter((offer) => offer.type === BankOfferTypes.KEYS && offer.keys !== null && isFreebie(offer.currency))
			.sort((a, b) => (a.keys ?? 0) - (b.keys ?? 0)),
	)

	ticketOffers: Signal<ShopBankOfferDto[]> = computed(() =>
		this.shopOffers()
			.filter(
				(offer) =>
					offer.type === BankOfferTypes.TICKETS && offer.tickets !== null && !isFreebie(offer.currency),
			)
			.sort((a, b) => (a.tickets ?? 0) - (b.tickets ?? 0)),
	)

	ticketFreebies: Signal<ShopBankOfferDto[]> = computed(() =>
		this.shopOffers()
			.filter(
				(offer) => offer.type === BankOfferTypes.TICKETS && offer.keys !== null && isFreebie(offer.currency),
			)
			.sort((a, b) => (a.tickets ?? 0) - (b.tickets ?? 0)),
	)

	packOffers: Signal<ShopBankOfferDto[]> = computed(() =>
		this.shopOffers()
			.filter((offer) => offer.type === BankOfferTypes.PACK && (offer.keys !== null || offer.tickets !== null))
			.sort((a, b) => a.price - b.price),
	)

	hasAdvertisementOffer: Signal<boolean> = computed(
		() => !!this.keyFreebies().find((offer) => offer.currency === BankOfferExtraCurrencyTypes.ADVERTISEMENT),
	)

	eventOffers: Signal<ShopBankEventOffers[]> = computed(() =>
		this.shopOffers()
			.filter((offer) => isEventType(offer.type) && (offer.keys !== null || offer.tickets !== null))
			.sort((a, b) => a.price - b.price)
			.reduce((result: ShopBankEventOffers[], offer) => {
				if (!offer.packName) {
					return result
				}

				const eventOffers = result.find((eventOffers) => eventOffers.name === offer.packName)
				if (!eventOffers) {
					result.push({ name: offer.packName, offers: [offer] })
				} else {
					eventOffers.offers.push(offer)
				}
				return result
			}, []),
	)
}
